import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

export const loadInvoiceProducts = async (values) => {
  const { default: saleApi } = await import("../../../services/sale.service");
  return saleApi.invoiceProducts(values);
};

const InvoiceProductPicker = ({ loadProducts = loadInvoiceProducts, onSelect, onCancel }) => {
  const [query, setQuery] = useState("");
  const [request, setRequest] = useState({ q: "", page: 1 });
  const [products, setProducts] = useState([]);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    setProducts([]);
    Promise.resolve().then(() => loadProducts({ ...request, limit: 20 })).then((response) => {
      if (!current) return;
      if (!Array.isArray(response?.data?.data)) throw new Error("Invalid product list");
      setProducts(response.data.data);
      setLastPage(response.data.last_page || 1);
    }).catch(() => {
      if (current) setError("Unable to load products. Please try searching again.");
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; };
  }, [request, loadProducts]);

  const search = () => setRequest({ q: query.trim(), page: 1 });
  return <section className="invoice-product-picker border rounded p-3 my-3" aria-label="Choose catalogue product">
    <label htmlFor="invoice-product-search">Search products</label>
    <div className="d-flex gap-2">
      <input id="invoice-product-search" className="form-control" maxLength={200} value={query}
        onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => {
          if (event.key === "Enter") { event.preventDefault(); search(); }
        }} />
      <button type="button" className="btn btn-outline-primary" onClick={search}>Search</button>
    </div>
    {loading && <p role="status">Loading products…</p>}
    {error && <p role="alert" className="text-danger">{error}</p>}
    {!loading && !error && !products.length && <p>No matching products.</p>}
    <div className="list-group my-2">
      {products.map((product) => <button key={product.id} type="button"
        className="list-group-item list-group-item-action" disabled={loading} onClick={() => onSelect(product)}>
        {product.name}{product.sku ? ` — ${product.sku}` : ""}{` — ${new Intl.NumberFormat("en-AU", {
          style: "currency", currency: product.retail_price?.currency || "AUD",
        }).format((product.retail_price?.amount_minor || 0) / 100)}`}
      </button>)}
    </div>
    <div className="d-flex flex-wrap gap-2">
      <button type="button" className="btn btn-outline-secondary" disabled={loading || request.page <= 1}
        onClick={() => setRequest((previous) => ({ ...previous, page: previous.page - 1 }))}>Previous products</button>
      <button type="button" className="btn btn-outline-secondary" disabled={loading || !!error || request.page >= lastPage}
        onClick={() => setRequest((previous) => ({ ...previous, page: previous.page + 1 }))}>Next products</button>
      <button type="button" className="btn btn-link" onClick={onCancel}>Cancel product selection</button>
    </div>
  </section>;
};

InvoiceProductPicker.propTypes = {
  loadProducts: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default InvoiceProductPicker;
